"use client";

import Image from "next/image";

import { useLanguage } from "../i18n/LanguageProvider";

/* ============================================================
   KLINIK KIRANA - KARTU TUNGGAL

   File ini menggantikan isi lama ClinicSlider.jsx. Nama file dan nama
   default export sengaja DIBIARKAN sama, supaya baris di page.jsx ini
   tetap bekerja tanpa diubah:

       import ClinicSlider from "./components/ClinicSlider";
       ...
       <ClinicSlider />

   Kalau nanti mau ganti nama jadi ClinicKirana.jsx, dua baris di page.jsx
   itu harus ikut diubah. Fungsional identik - murni soal kerapian nama.

   RS JMC hanya punya satu faskes tingkat I, jadi tidak ada yang digeser.

   YANG DIHAPUS DARI VERSI SLIDER:
   - "use client" : komponen ini tidak lagi punya state, ref, maupun event
     handler, jadi bisa dirender di server. CATATAN: page.jsx kamu sendiri
     masih "use client", jadi komponen ini tetap ikut terbundel ke browser.
     Bukan error, hanya penghematannya belum terasa sampai page.jsx
     dipecah.
   - useState / useRef / useEffect / useCallback beserta logika membaca
     kartu terdekat dari posisi scroll.
   - CARD_WIDTH & TRACK_PADDING : lebar 86%/70%/56% plus padding
     penyeimbang ada supaya kartu tetangga mengintip. Tidak ada tetangga.
   - DUPLICATE_COUNT, array CLINICS, dan properti `id`. `id` dulu wajib
     karena dipakai sebagai React key di .map(); tidak ada .map() lagi.
   - panah kiri/kanan, titik indikator, penghitung "1 / 5", handler
     ArrowLeft/ArrowRight.
   - role="region" + aria-roledescription="carousel". Menyebut satu kartu
     sebagai carousel menyesatkan pengguna pembaca layar.
   - prop `active` beserta tabIndex={active ? 0 : -1}. Itu ada untuk
     mencegah Tab melompat ke tautan di kartu yang sedang di luar layar.
     Sekarang seluruh isi kartu selalu terlihat, jadi semua tautan harus
     bisa difokus secara normal.
   ============================================================ */

/* `name` TIDAK diterjemahkan: "Klinik Kirana" nama resmi, sama di
   semua bahasa. Nomor telepon dan kode faskes juga tetap - keduanya
   pengenal, bukan kalimat. Yang berpindah ke kamus hanya teks yang
   dibaca sebagai bahasa. */
const KIRANA = {
  name: "Klinik Kirana",
  tierKey: "klinik.tier",

  image: "/images/klinik.jpg",
  imageFit: "contain",
  imageAltKey: "klinik.imageAlt",

  descriptionKey: "klinik.deskripsi",
  faskesCode: "0112B160",
  phoneLabel: "0821 2397 2332",
  phoneHref: "tel:+6282123972332",
};


export default function ClinicSlider() {
  /* TIDAK ADA px DI SINI. Padding kiri-kanan sudah dipegang oleh
     <section> di page.jsx, dan header di section itu memakai wadah yang
     sama persis: mx-auto max-w-4xl. Itu yang bikin tepi kiri kartu
     sejajar dengan tepi kiri judul di semua ukuran layar.

     Kalau padding ditulis di kedua tempat, nilainya bertumpuk dan kartu
     jadi lebih sempit dari judulnya.

     max-w-4xl (896px) kira-kira selebar kartu aktif di versi slider pada
     layar desktop, dan cukup untuk menahan kartu supaya tidak melebar
     berlebihan di monitor besar. Kalau angka ini diubah, ubah juga
     max-w-4xl pada wadah header di page.jsx. */
  const { t } = useLanguage();

  return (
    <div className="mx-auto w-full max-w-4xl">
      <ClinicCard clinic={KIRANA} t={t} />
    </div>
  );
}

/* ============================================================
   CLINIC CARD
   ============================================================ */

function ClinicCard({ clinic, t }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-[var(--c-surface)] shadow-[0_1px_2px_rgba(47,4,37,0.3),0_10px_20px_-8px_rgba(47,4,37,0.35),0_28px_50px_-16px_rgba(47,4,37,0.45)] ring-1 ring-black/5 sm:rounded-[28px]">
      {/* ============================================================
          BINGKAI GAMBAR - POLA "MAT" SEPERTI CETAKAN BERPIGURA

          Masalah yang diperbaiki: poster ini punya latar putih berbunga
          sendiri, dan sebelumnya ia menempel sampai tepi kartu yang juga
          putih. Akibatnya tepi poster larut ke badan kartu - tidak ada
          garis yang memberi tahu mata di mana gambar berakhir dan kartu
          dimulai.

          Bingkainya dua lapis, dan sengaja tidak lebih:

          1. MAT (div luar). Bidang plum #f1faf5 tipis di sekeliling gambar.
             Warna ini dipilih karena poster berlatar putih - mat putih
             tidak akan terlihat sama sekali. #f1faf5 cukup untuk
             memisahkan, dan masih satu keluarga dengan warna merek.

          2. GARIS TEPI (ring-inset pada div dalam). Satu garis rambut
             plum gelap 10% yang menegaskan batas gambar.

             `ring-inset`, BUKAN `border`. Border digambar di luar kotak
             dan pada kotak ber-radius sering menyisakan celah rambut di
             sudut saat halaman di-zoom. Ring inset digambar di dalam
             kotak ber-radius yang sama, jadi ia menempel rapat.

          YANG SENGAJA TIDAK DIPAKAI: garis emas, bingkai ganda, bevel,
          atau bayangan tebal di sekeliling gambar. Di situs rumah sakit
          hiasan semacam itu terbaca seperti template lama. Yang membuat
          bingkai terlihat profesional adalah jarak yang konsisten dan
          satu garis tipis, bukan ornamen.

          Padding mat naik bertahap (12px -> 16px -> 20px) supaya di layar
          kecil ia tidak memakan lebar gambar terlalu banyak.
          ============================================================ */}
      <div className="bg-[var(--c-tint)]/70 p-3 sm:p-4 md:p-5">

        {/* RASIO TETAP DIKUNCI, sama seperti versi slider.

            Alasan aslinya - menyamakan tinggi antar kartu - memang sudah
            hilang. Tapi rasio terkunci juga berarti file gambar apa pun
            bisa masuk tanpa merusak layout, dan poster Kirana sudah pas
            di kotak ini.

            "contain" : poster tampil utuh, muncul pita kosong di samping.
            "cover"   : mengisi penuh, sebagian terpotong - untuk foto
                        gedung, JANGAN untuk poster berteks (nomor HP di
                        baris paling atas poster Kirana akan terpotong).

            bg-[var(--c-surface)] di sini, bukan bg-[var(--c-line-soft)]. Pita kosong dari
            "contain" jadi menyatu dengan latar poster, sementara mat
            plum di luar yang memberi batasnya. */}
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-[var(--c-surface)] ring-1 ring-inset ring-[var(--c-line)]/10 sm:aspect-[16/9] sm:rounded-xl">
          {clinic.image ? (
            <Image
              src={clinic.image}
              fill
              /* Sedikit lebih kecil dari lebar kartu (896px) karena mat
                 memakan padding di kiri-kanan. Angka ini hanya petunjuk
                 bagi browser saat memilih ukuran berkas; meleset sedikit
                 tidak merusak apa pun. */
              sizes="(max-width: 896px) 100vw, 856px"
              alt={t(clinic.imageAltKey)}
              className={
                clinic.imageFit === "contain"
                  ? "object-contain object-center"
                  : "object-cover object-center"
              }
            />
          ) : (
            /* Blok penampung kalau path gambar dikosongkan. Tanpa ini,
               next/image melempar error dan halaman ikut rusak. */
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[var(--c-surface-2)] text-[var(--c-faint)]">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="8.5" cy="10" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="m4 17 4.5-4.5 3 3L15 12l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-xs font-medium">{t("klinik.fotoKosong")}</span>
            </div>
          )}
        </div>

      </div>

      {/* Garis pemisah diubah dari slate-100 ke #def2e8 supaya sewarna
         dengan mat di atasnya. Garis abu-abu di bawah bidang plum muda
         terbaca kotor - dua netral berbeda yang bertemu di satu titik. */}
      <div className="grid gap-5 border-t border-[var(--c-brand-line-2)] p-5 sm:gap-6 sm:p-8 md:grid-cols-[1fr_auto] md:items-end md:gap-10 md:p-10">
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em]">
            {t(clinic.tierKey)}
          </p>

          <h3 className="mb-2 text-xl font-bold leading-tight text-[var(--c-ink)] sm:mb-3 sm:text-2xl md:text-3xl">
            {clinic.name}
          </h3>

          <p className="mb-4 max-w-xl text-[13px] leading-relaxed text-[var(--c-body)] sm:mb-5 sm:text-sm md:text-base">
            {t(clinic.descriptionKey)}
          </p>

          <dl className="flex flex-col gap-2 text-[13px] sm:flex-row sm:flex-wrap sm:gap-x-10 sm:gap-y-2 sm:text-sm">
            <div className="flex gap-3">
              <dt className="w-24 shrink-0 text-[var(--c-muted)] sm:w-auto">{t("klinik.kodeFaskes")}</dt>
              <dd className="font-semibold tabular-nums text-[var(--c-ink)]">
                {clinic.faskesCode}
              </dd>
            </div>

            <div className="flex gap-3">
              <dt className="w-24 shrink-0 text-[var(--c-muted)] sm:w-auto">{t("klinik.telepon")}</dt>
              <dd>
                <a
                  href={clinic.phoneHref}
                  className="font-semibold text-[var(--c-brand)] underline-offset-4 hover:underline"
                >
                  {clinic.phoneLabel}
                </a>
              </dd>
            </div>
          </dl>
        </div>

        <a
          href={clinic.phoneHref}
          className="inline-flex w-full shrink-0 items-center justify-center rounded-xl bg-[var(--c-brand)] px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-[var(--c-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)] sm:w-fit sm:rounded-2xl sm:py-3"
        >
          {t("klinik.hubungi")}
        </a>
      </div>
    </div>
  );
}
